import { FormEvent } from 'react';
import { advancedDefaults, filters } from '../searchConfig';
import { AdvancedFilters } from '../types';

type SearchFormProps = {
  advancedFilters: AdvancedFilters;
  category: string;
  query: string;
  showAdvanced: boolean;
  onCategoryChange: (category: string) => void;
  onClearSearch: () => void;
  onQueryChange: (query: string) => void;
  onResetAdvancedFilters: () => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  onToggleAdvanced: () => void;
  onUpdateAdvancedFilter: (name: keyof typeof advancedDefaults, value: string) => void;
};

export function SearchForm({
  advancedFilters,
  category,
  query,
  showAdvanced,
  onCategoryChange,
  onClearSearch,
  onQueryChange,
  onResetAdvancedFilters,
  onSubmit,
  onToggleAdvanced,
  onUpdateAdvancedFilter,
}: SearchFormProps) {
  return (
    <form className="search-form" role="search" onSubmit={onSubmit}>
      <label className="sr-only" htmlFor="search-input">
        Search for
      </label>
      <div className="search-box">
        <input
          id="search-input"
          name="q"
          type="search"
          placeholder="Search for..."
          autoComplete="off"
          autoCapitalize="none"
          spellCheck="false"
          value={query}
          onChange={(event) => onQueryChange(event.target.value)}
        />
        {query && (
          <button className="icon-button clear-button" type="button" aria-label="Clear search" onClick={onClearSearch}>
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="m6 6 12 12M18 6 6 18" />
            </svg>
          </button>
        )}
        <button className="icon-button search-button" type="submit" aria-label="Search">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="m21 21-4.35-4.35" />
            <circle cx="11" cy="11" r="7" />
          </svg>
        </button>
      </div>

      <div className="filter-row" aria-label="Search categories">
        {filters.map((filter) => (
          <button
            key={filter.value}
            className={category === filter.value ? 'is-active' : ''}
            type="button"
            onClick={() => onCategoryChange(filter.value)}
          >
            {filter.label}
          </button>
        ))}
      </div>

      <div className="advanced-toggle-row">
        <button
          className="advanced-toggle"
          type="button"
          aria-expanded={showAdvanced}
          onClick={onToggleAdvanced}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M4 7h10" />
            <path d="M18 7h2" />
            <path d="M16 5v4" />
            <path d="M4 17h2" />
            <path d="M10 17h10" />
            <path d="M8 15v4" />
          </svg>
          <span>Advanced</span>
        </button>
      </div>

      {showAdvanced && (
        <fieldset className="advanced-panel">
          <legend className="sr-only">Advanced search filters</legend>
          <label>
            <span>Language</span>
            <select
              value={advancedFilters.language}
              onChange={(event) => onUpdateAdvancedFilter('language', event.target.value)}
            >
              <option value="auto">Automatic</option>
              <option value="en">English</option>
              <option value="es">Spanish</option>
              <option value="fr">French</option>
              <option value="de">German</option>
            </select>
          </label>
          <label>
            <span>Time</span>
            <select
              value={advancedFilters.timeRange}
              onChange={(event) => onUpdateAdvancedFilter('timeRange', event.target.value)}
            >
              <option value="">Any time</option>
              <option value="day">Past day</option>
              <option value="week">Past week</option>
              <option value="month">Past month</option>
              <option value="year">Past year</option>
            </select>
          </label>
          <label>
            <span>Safe search</span>
            <select
              value={advancedFilters.safeSearch}
              onChange={(event) => onUpdateAdvancedFilter('safeSearch', event.target.value)}
            >
              <option value="0">Off</option>
              <option value="1">Moderate</option>
              <option value="2">Strict</option>
            </select>
          </label>
          <button type="button" onClick={onResetAdvancedFilters}>
            Reset
          </button>
        </fieldset>
      )}
    </form>
  );
}
